import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "danger" | "ghost" | "outline";
type Size = "sm" | "md" | "lg";

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

// Disabled is ONE look in both themes: faded to 40% with the hover change
// suppressed. The filled variants used to swap to another shade instead, and
// in dark mode that shade was as strong as the live button — a disabled Kill
// rendered down-700, darker and more "pressable" than the enabled down-600.
const variants: Record<Variant, string> = {
  primary:
    "bg-brand-600 text-white hover:bg-brand-700 disabled:hover:bg-brand-600",
  secondary:
    "bg-surface-2 text-fg hover:bg-border disabled:hover:bg-surface-2 border border-border",
  outline:
    "bg-transparent text-fg hover:bg-surface-2 disabled:hover:bg-transparent border border-border",
  danger:
    "bg-down-600 text-white hover:bg-down-700 disabled:hover:bg-down-600",
  ghost:
    "bg-transparent text-fg-subtle hover:bg-surface-2 hover:text-fg disabled:hover:bg-transparent disabled:hover:text-fg-subtle",
};

const sizes: Record<Size, string> = {
  sm: "text-xs px-2.5 py-1.5 gap-1.5",
  md: "text-sm px-3.5 py-2 gap-2",
  lg: "text-sm px-4 py-2.5 gap-2",
};

export default function Button({
  variant = "primary",
  size = "md",
  className = "",
  iconLeft,
  iconRight,
  children,
  ...rest
}: Props) {
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center font-medium rounded transition-colors ${variants[variant]} ${sizes[size]} disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    >
      {iconLeft && <span className="shrink-0">{iconLeft}</span>}
      {children}
      {iconRight && <span className="shrink-0">{iconRight}</span>}
    </button>
  );
}
